/**
 * custom-size-memory.js
 * Sothiares — remember the customer's size for the current visit
 *
 * Behaviour:
 *   When a customer picks a size on a product page, remember it. On any other
 *   product page that offers the same size, pre-select it for them.
 *
 * Deliberate limits (agreed with Noel, 2026-08-12):
 *   1. Only applies when the size is genuinely purchasable. If the remembered
 *      size is sold out on this product, we leave the page exactly as the theme
 *      rendered it. We never put a customer on a size they cannot buy, and we
 *      never silently substitute a different size.
 *   2. Never auto-selects a pre-order variant. Per PROJECT.md, pre-order copy
 *      must appear only on genuinely unavailable sizes so that in-stock sizes
 *      keep their urgency. Auto-selecting a pre-order size would make pre-order
 *      the default view on landing and undercut that.
 *   3. localStorage with a rolling 30-day expiry. Changed from session-only on
 *      2026-08-12 at Noel's request, after the trade-off was put to him:
 *      returning customers get their size pre-filled, at the cost of a stale
 *      preference potentially applying weeks later to someone buying a gift in
 *      another size. The clock resets every time the customer actively picks a
 *      size, so 30 days means 30 days of inactivity, not 30 days from first use.
 *
 *      Guards 1 and 2 are what keep this safe: an expired or stale size is only
 *      ever applied when it is in stock and not a pre-order, and it is never
 *      substituted for a different size.
 *   4. Product pages only — not quick-add modals or collection cards. A silent
 *      pre-selection inside a modal the customer barely looks at is the same
 *      class of risk as the wrong-size bug this shipped alongside.
 *
 * Why we can't read pre-order status from the product JSON:
 *   /products/{handle}.js omits inventory_policy and inventory_quantity, so an
 *   out-of-stock-but-continue-selling variant is indistinguishable from an
 *   in-stock one (both report available: true). The pre-order variant IDs are
 *   instead read from <pre-order-block data-pre-order-variant-ids>, which
 *   blocks/buy-buttons.liquid already computes in Liquid where the inventory
 *   fields are available.
 *
 * Depends on custom-variant-sync.js being loaded first: applying the selection
 * fires a real `change` event, and that file's capture-phase listener is what
 * writes the variant ID into the form synchronously.
 *
 * Author: Sothiares dev
 * Date: 2026-08-12
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'sothiares:remembered-size';
  var SIZE_OPTION_PATTERN = /size/i;
  var MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

  /**
   * @param {string} value
   * @returns {string} comparison-safe form of an option value
   */
  function normalise(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  /**
   * Reads the remembered size, discarding it if it has expired.
   * Anything unparseable or missing an expiry is treated as absent rather than
   * trusted — a malformed entry must never resolve to a size we'd auto-apply.
   * @returns {string|null}
   */
  function readRemembered() {
    var raw;

    try {
      raw = window.localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null; // Safari private mode, storage disabled entirely.
    }

    if (!raw) return null;

    var entry = null;
    try {
      entry = JSON.parse(raw);
    } catch (e) {
      entry = null; // Not JSON — fall through to the purge below.
    }

    var usable = entry && typeof entry.value === 'string' && typeof entry.expires === 'number';

    // Purge anything we can't use, not just expired entries. An unparseable
    // value left in place would sit there permanently and silently disable the
    // feature for that customer, with nothing to indicate why.
    if (!usable || Date.now() > entry.expires) {
      discardRemembered();
      return null;
    }

    return entry.value;
  }

  function discardRemembered() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* Nothing sensible to do if storage itself is unavailable. */
    }
  }

  /**
   * Stores the size with a fresh 30-day expiry. The clock restarts on every
   * deliberate size choice, so the window measures inactivity rather than
   * time since the customer first used the site.
   * @param {string} value
   */
  function writeRemembered(value) {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ value: value, expires: Date.now() + MAX_AGE_MS })
      );
    } catch (e) {
      /* Non-fatal. The feature simply does nothing. */
    }
  }

  /**
   * Reads an option group's name ("Size", "Colour", ...).
   * Handles both the button/swatch style (legend) and the dropdown style (label).
   * @param {Element} group
   * @returns {string}
   */
  function optionNameOf(group) {
    var source = group.querySelector('legend') || group.querySelector('label');
    if (!source) return '';

    // The swatch style appends the selected value inside the legend
    // (<span class="variant-option__swatch-value">Navy</span>) — strip it so we
    // compare against the option name alone.
    var clone = source.cloneNode(true);
    var swatchValue = clone.querySelector('.variant-option__swatch-value');
    if (swatchValue && swatchValue.parentNode) swatchValue.parentNode.removeChild(swatchValue);

    return clone.textContent.replace(/\s+/g, ' ').trim();
  }

  /**
   * @param {Element} group
   * @returns {boolean}
   */
  function isSizeGroup(group) {
    return SIZE_OPTION_PATTERN.test(optionNameOf(group));
  }

  /**
   * @param {Element} picker
   * @returns {Element|null} the size option group within a picker
   */
  function sizeGroupOf(picker) {
    var groups = picker.querySelectorAll('fieldset, .variant-option--dropdowns');
    for (var i = 0; i < groups.length; i++) {
      if (isSizeGroup(groups[i])) return groups[i];
    }
    return null;
  }

  /**
   * Variant IDs the pre-order block has flagged as pre-order on this page.
   * @returns {Set<string>}
   */
  function preOrderVariantIds() {
    var ids = new Set();
    var blocks = document.querySelectorAll('pre-order-block[data-pre-order-variant-ids]');

    Array.prototype.forEach.call(blocks, function (block) {
      var raw = block.getAttribute('data-pre-order-variant-ids') || '';
      raw.split(',').forEach(function (id) {
        var trimmed = id.trim();
        if (trimmed) ids.add(trimmed);
      });
    });

    return ids;
  }

  /**
   * True only for the main product-page picker.
   * @param {Element} picker
   */
  function isMainProductPicker(picker) {
    if (!/\/products\//.test(window.location.pathname)) return false;
    return !picker.closest('quick-add-component, quick-add-dialog, product-card, .quick-add-modal');
  }

  /* -- Remember ----------------------------------------------------------- */
  document.addEventListener(
    'change',
    function (event) {
      var input = event.target;
      if (!input || !input.closest) return;

      var picker = input.closest('variant-picker');
      if (!picker) return;

      var group = input.closest('fieldset, .variant-option--dropdowns');
      if (!group || !isSizeGroup(group)) return;

      var value = input.value;
      if (value) writeRemembered(value);
    },
    false
  );

  /* -- Apply -------------------------------------------------------------- */
  function applyRememberedSize() {
    var remembered = readRemembered();
    if (!remembered) return;

    var preOrderIds = preOrderVariantIds();
    var pickers = document.querySelectorAll('variant-picker');

    Array.prototype.forEach.call(pickers, function (picker) {
      if (!isMainProductPicker(picker)) return;
      if (picker.getAttribute('data-size-memory-applied')) return;

      var group = sizeGroupOf(picker);
      if (!group) return;

      var inputs = group.querySelectorAll('input[type="radio"]');
      var match = null;

      for (var i = 0; i < inputs.length; i++) {
        if (normalise(inputs[i].value) === normalise(remembered)) {
          match = inputs[i];
          break;
        }
      }

      // This product doesn't offer that size at all — leave the page alone.
      if (!match) return;

      // Mark before any early return below: we've made our one decision for
      // this picker and must not reconsider it on a later pass.
      picker.setAttribute('data-size-memory-applied', '1');

      if (match.checked) return; // already the default, nothing to do
      if (match.getAttribute('data-option-available') === 'false') return; // sold out
      if (match.dataset.variantId && preOrderIds.has(match.dataset.variantId)) return; // pre-order

      // A real click, so VariantPicker.variantChanged runs its normal
      // re-render AND custom-variant-sync.js writes the form ID synchronously.
      match.click();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyRememberedSize);
  } else {
    applyRememberedSize();
  }
})();
